// Shared loading-skeleton primitives. Gray pulsing placeholders that mimic a
// page's real layout so loads feel fast and intentional rather than blank.
// Respects prefers-reduced-motion (Tailwind `motion-reduce:animate-none`).

interface SkeletonProps {
  className?: string;
}

/** A single pulsing placeholder block. Pass sizing via className. */
export function Skeleton({ className = "" }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse motion-reduce:animate-none rounded bg-gray-200 dark:bg-gray-800 ${className}`}
      aria-hidden="true"
    />
  );
}

/** A bordered card skeleton: a title line plus a couple of detail lines. */
export function CardSkeleton({ lines = 2 }: { lines?: number }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-16" />
      </div>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={`mt-2 h-3 ${i % 2 ? "w-1/2" : "w-3/4"}`} />
      ))}
    </div>
  );
}

/**
 * A vertical list of card skeletons — the default loading state for the
 * list-style panes (Logs, Medications, Allergies, Trash, …).
 */
export function ListSkeleton({ rows = 4, lines = 2 }: { rows?: number; lines?: number }) {
  return (
    <div className="space-y-2" role="status" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <CardSkeleton key={i} lines={lines} />
      ))}
      <span className="sr-only">Loading…</span>
    </div>
  );
}

/** A centered full-page skeleton with a heading bar and a list. */
export function PageSkeleton() {
  return (
    <div className="mx-auto w-full max-w-3xl p-4" role="status" aria-label="Loading">
      <Skeleton className="mb-4 h-5 w-40" />
      <ListSkeleton rows={5} />
      <span className="sr-only">Loading…</span>
    </div>
  );
}
